import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CreateLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate?: (body: { name: string; description?: string }) => Promise<void>;
}

export function CreateLibraryDialog({
  open,
  onOpenChange,
  onCreate,
}: CreateLibraryDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!name.trim() || !onCreate) return;

    setSaving(true);
    setError(null);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      setName("");
      setDescription("");
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create library");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-glass-border bg-[#111b35] text-white sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Create Library</DialogTitle>
          <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
            Libraries organize documents and chats within a workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="library-name" className="text-slate-400">
              Name
            </Label>
            <Input
              id="library-name"
              placeholder="e.g. Contracts 2024"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="library-desc" className="text-slate-400">
              Description (optional)
            </Label>
            <Input
              id="library-desc"
              placeholder="What documents belong here?"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="default" onClick={() => void handleCreate()} disabled={saving || !name.trim()} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Library
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
